const lockTimeout = 200;
const defaultMaxLeaseDuration = 300;
const mutexPrefix = "LSEM";
const delayMinimumMs = 15;
const delayDriftRangeMs = 15;

export interface StagedLockLease {
  instanceIdentifier: string;
  startTime: number; // getTime()
  maxDuration: number; // ms
}

// Based on https://medium.engineering/wait-dont-touch-that-a211832adc3a
//  Which is based on : https://www.microsoft.com/en-us/research/publication/fast-mutual-exclusion-algorithm/?from=http%3A%2F%2Fresearch.microsoft.com%2Fen-us%2Fum%2Fpeople%2Flamport%2Fpubs%2Ffast-mutex.pdf
// 1.  Set X = i
// 2.  If Y != 0: Restart
// 3.  Set Y = i
// 4.  If X != i:
// 5.    Delay
// 6.    If Y != i: Restart
// 7.  [Has lock, do work]
// 8.  Set Y = 0
export class LocalStorageEmulatedMutex {
  constructor(public prefix: string, public instanceIdentifier: string) {}

  // invoker must be sync
  executeLockedCode(invoker: () => void): Promise<void> {
    return this.attemptAcquireLock().then(() => {
      invoker();
      return Promise.resolve();
    });
  }

  private getDelayWithDrift(): number {
    return delayMinimumMs + Math.round(delayDriftRangeMs * Math.random());
  }

  private attemptAcquireLock(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.lockWithRetry(new Date().getTime(), lockTimeout, resolve, reject);
    });
  }

  private lockWithRetry(
    startTime: number,
    lockTimeout: number,
    resolver: () => void,
    rejecter: (message: string) => void
  ) {
    this.checkStage2KeyHealth();
    if (lockTimeout < new Date().getTime() - startTime) {
      rejecter("Lock could not be achieved in a suitable amount of time.");
      return;
    }

    let invokeCriticalSection = () => {
      // HAS LOCK DO WORK
      try {
        resolver();
      } finally {
        this.freeStage2Key();
      }
    };

    this.leaseStage1Key();
    if (this.isStage2KeyOccupied()) {
      this.delay(this.getDelayWithDrift()).then(() => {
        this.lockWithRetry(startTime, lockTimeout, resolver, rejecter);
      });
    } else {
      this.leaseStage2Key();
      if (!this.isStage1KeyLeasedByMe()) {
        this.delay(this.getDelayWithDrift()).then(() => {
          if (this.doesCurrentLeaseIndicateRetryNeeded()) {
            this.lockWithRetry(startTime, lockTimeout, resolver, rejecter);
          } else {
            invokeCriticalSection();
          }
        });
      } else {
        invokeCriticalSection();
      }
    }
  }

  private doesCurrentLeaseIndicateRetryNeeded(): boolean {
    return (
      (this.getStage2KeyLeaseStatus() & LeaseStatus.LeasedByMe) != LeaseStatus.LeasedByMe ||
      (this.getStage2KeyLeaseStatus() & LeaseStatus.Expired) == LeaseStatus.Expired
    );
  }

  private checkStage2KeyHealth() {
    // Uses the modified Peter lateness algorithm :
    //  Have Stage2 keep track of claim initialization time AND register a requested Lease time. Anytime the lease time is exceeded, allow reclamation of Stage2 for use.
    //  Dead windows inform immediate Stage2 reclamation.
    let value = localStorage.getItem(this.stage2Key);
    let clearLease = false;
    try {
      let lease = <StagedLockLease>JSON.parse(value);
      if (this.isLeaseExpired(lease)) {
        clearLease = true;
      }
    } catch (error) {
      clearLease = true;
    }

    if (clearLease) {
      localStorage.removeItem(this.stage2Key);
    }
  }

  private delay(ms: number) {
    let promise = new Promise(function (resolve, reject) {
      setTimeout(resolve, ms);
    });
    return promise;
  }

  public get stage1Key(): string {
    return `${mutexPrefix}_${this.prefix}_STAGE_1_MUTEX_KEY`;
  }

  public get stage2Key(): string {
    return `${mutexPrefix}_${this.prefix}_STAGE_2_MUTEX_KEY`;
  }

  private leaseStage1Key() {
    localStorage.setItem(this.stage1Key, this.instanceIdentifier);
  }

  private isStage2KeyOccupied(): boolean {
    return !!localStorage.getItem(this.stage2Key);
  }

  private leaseStage2Key() {
    localStorage.setItem(
      this.stage2Key,
      JSON.stringify(<StagedLockLease>{
        instanceIdentifier: this.instanceIdentifier,
        maxDuration: defaultMaxLeaseDuration,
        startTime: new Date().getTime()
      })
    );
  }

  private isStage1KeyLeasedByMe(): boolean {
    return localStorage.getItem(this.stage1Key) == this.instanceIdentifier;
  }

  private getStage2KeyLeaseStatus(): LeaseStatus {
    let status = LeaseStatus.NotLeased;
    let value = localStorage.getItem(this.stage2Key);
    try {
      let lease = <StagedLockLease>JSON.parse(value);
      if (lease.instanceIdentifier == this.instanceIdentifier) {
        status |= LeaseStatus.LeasedByMe;
      }

      if (this.isLeaseExpired(lease)) {
        status |= LeaseStatus.Expired;
      }
    } catch (error) {
      status = LeaseStatus.NotLeased;
    }

    return status;
  }

  private freeStage2Key() {
    localStorage.removeItem(this.stage2Key);
  }

  private isLeaseExpired(lease: StagedLockLease) {
    return lease.maxDuration < new Date().getTime() - lease.startTime;
  }
}

enum LeaseStatus {
  NotLeased = 0,
  LeasedByMe = 1 << 0, // 0001
  Expired = 2 << 0 // 0002
}
